/**
 * FramePhaseBudget — measured TIME per frame phase, in milliseconds.
 *
 * Deliberate counterpart to AllocCounters/`attribution.topCounters`, which
 * report per-second FREQUENCY. The 2026-07-22 organic-freeze incident forbids
 * optimizing the highest-frequency counter, because throughput leads are not
 * CPU ownership — but until now frequency was the only ranked signal a reader
 * had. This module is the signal you ARE allowed to act on.
 *
 * Cost is one add per phase per frame; the drain is O(phases).
 */

/** `react`: commit time of the profiled game UI tree (dev builds; see reactCommitBudget). */
export type FramePhase = 'scene' | 'tween' | 'render' | 'react';

const accumulated = new Map<FramePhase, number>();
/**
 * When the accumulator was last emptied. The window a drain must normalise by
 * is "time since the previous drain", NOT whatever the caller believes.
 *
 * The 2026-07-25 bug: `drain(dtMs)` was called only when a freeze fired, but
 * was handed the ~1s AllocCounters sample window. Two freezes 8s apart meant 8s
 * of accumulated work divided by 1s — an 8x inflation that reported `render` at
 * 980 ms/s on a client whose `lastRenderMs` was 0.54ms at 169fps (~91 ms/s
 * true). It made the render phase look like it owned the machine.
 */
let lastDrainAt: number | null = null;

export const FramePhaseBudget = {
  /** Add a measured duration for one phase of one frame. */
  record(phase: FramePhase, ms: number, now: number = performance.now()): void {
    if (!Number.isFinite(ms) || ms <= 0) return;
    // The first sample after a drain marks where this window opened.
    if (lastDrainAt === null) lastDrainAt = now;
    accumulated.set(phase, (accumulated.get(phase) ?? 0) + ms);
  },

  /**
   * Normalise the accumulation to ms-of-work-per-second-of-wall-clock and clear
   * it. Ranked descending by measured time.
   *
   * The window is measured internally from the previous drain, because that is
   * the period the accumulator actually covers. `fallbackWindowMs` is used only
   * for the very first drain, when there is no previous one to measure from.
   *
   * A phase can legitimately exceed 1000 ms/s only if it is re-entered or
   * double-counted; treat anything near or above that as an instrumentation
   * bug, not a finding.
   */
  drain(
    fallbackWindowMs: number,
    now: number = performance.now(),
  ): { phase: FramePhase; msPerSec: number }[] {
    const measured = lastDrainAt === null ? fallbackWindowMs : now - lastDrainAt;
    const windowMs = Number.isFinite(measured) && measured > 0 ? measured : fallbackWindowMs;
    lastDrainAt = null;
    if (!Number.isFinite(windowMs) || windowMs <= 0) {
      accumulated.clear();
      return [];
    }
    const out: { phase: FramePhase; msPerSec: number }[] = [];
    for (const [phase, ms] of accumulated) {
      out.push({ phase, msPerSec: Math.round((ms / windowMs) * 1000) });
    }
    accumulated.clear();
    out.sort((a, b) => b.msPerSec - a.msPerSec);
    return out;
  },

  /** Test hook. */
  reset(): void {
    accumulated.clear();
    lastDrainAt = null;
  },
};
